import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { bannersService } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';

const BannersListPage = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sortBy: 'priority',
    sortOrder: 'DESC'
  });

  useEffect(() => {
    loadBanners();
  }, [pagination.page, pagination.limit, filters]);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await bannersService.getAllBanners(params);

      if (response.success) {
        setBanners(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Banner\'lar yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading banners:', error);
      toast.error('Banner\'lar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (bannerId, title) => {
    if (!window.confirm(`"${title}" banner'ını silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const response = await bannersService.deleteBanner(bannerId);
      if (response.success) {
        toast.success('Banner başarıyla silindi');
        loadBanners();
      } else {
        toast.error(response.error || 'Banner silinemedi');
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error('Banner silinirken bir hata oluştu');
    }
  };

  const handleToggleStatus = async (banner) => {
    const newStatus = banner.status === 'active' ? 'inactive' : 'active';
    try {
      const formData = new FormData();
      formData.append('status', newStatus);

      const response = await bannersService.updateBanner(banner.id, formData);
      if (response.success) {
        toast.success('Banner durumu güncellendi');
        loadBanners();
      } else {
        toast.error(response.error || 'Banner durumu güncellenemedi');
      }
    } catch (error) {
      console.error('Error updating banner status:', error);
      toast.error('Banner durumu güncellenirken bir hata oluştu');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getBannerImageUrl = (imageUrl) => {
    // DigitalOcean Spaces URL'i oluştur
    const spacesEndpoint = process.env.REACT_APP_SPACES_ENDPOINT || 'https://randuapp.nyc3.digitaloceanspaces.com';
    return `${spacesEndpoint}/${imageUrl}`;
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Banner'lar" />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">Banner Listesi</h5>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/banners/add')}
          >
            <Icon icon="solar:add-circle-outline" className="me-2" />
            Banner Ekle
          </button>
        </div>
        <div className="card-body">
          {/* Filtreler */}
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Durum</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Tümü</option>
                <option value="active">Aktif</option>
                <option value="inactive">Pasif</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Arama</label>
              <input
                type="text"
                className="form-control"
                placeholder="Başlık, açıklama ara..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Sıralama</label>
              <select
                className="form-select"
                value={`${filters.sortBy}_${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('_');
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
              >
                <option value="priority_DESC">Öncelik (Yüksek → Düşük)</option>
                <option value="priority_ASC">Öncelik (Düşük → Yüksek)</option>
                <option value="created_at_DESC">Tarih (Yeni → Eski)</option>
                <option value="created_at_ASC">Tarih (Eski → Yeni)</option>
              </select>
            </div>
          </div>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : banners.length === 0 ? (
            <div className="alert alert-info text-center">
              Banner bulunamadı.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th width="150">Görsel</th>
                      <th>Başlık</th>
                      <th>Link</th>
                      <th>Öncelik</th>
                      <th>Durum</th>
                      <th>Başlangıç</th>
                      <th>Bitiş</th>
                      <th className="text-end">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banners.map(banner => (
                      <tr key={banner.id}>
                        <td>
                          <img
                            src={getBannerImageUrl(banner.image_url)}
                            alt={banner.title}
                            style={{ width: '100px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              e.target.src = '/assets/images/placeholder.png';
                            }}
                          />
                        </td>
                        <td>
                          <div>
                            <strong>{banner.title}</strong>
                            {banner.description && (
                              <div className="text-muted small">{banner.description}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          {banner.link_url ? (
                            <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="text-primary">
                              <Icon icon="solar:link-outline" className="me-1" />
                              Link
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-secondary">{banner.priority}</span>
                        </td>
                        <td>
                          <button
                            className={`btn btn-sm ${banner.status === 'active' ? 'btn-success' : 'btn-secondary'}`}
                            onClick={() => handleToggleStatus(banner)}
                          >
                            {banner.status === 'active' ? 'Aktif' : 'Pasif'}
                          </button>
                        </td>
                        <td>{formatDate(banner.start_date)}</td>
                        <td>{formatDate(banner.end_date)}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => navigate(`/banners/edit/${banner.id}`)}
                          >
                            <Icon icon="solar:pen-outline" className="me-1" />
                            Düzenle
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(banner.id, banner.title)}
                          >
                            <Icon icon="solar:trash-bin-outline" className="me-1" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', display: 'inline-block' }}
                    value={pagination.limit}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ms-2 text-muted">
                    Toplam {pagination.total} kayıt
                  </span>
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Önceki
                      </button>
                    </li>
                    {[...Array(pagination.totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.page - 2 && page <= pagination.page + 2)
                      ) {
                        return (
                          <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      } else if (
                        page === pagination.page - 3 ||
                        page === pagination.page + 3
                      ) {
                        return (
                          <li key={page} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Sonraki
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default BannersListPage;

